import React, { useState, useEffect, useMemo } from "react";
import { FileUploader } from "react-drag-drop-files";
import { BsArrowRight, BsCheck, BsPencilFill } from "react-icons/bs";
import { ImBin } from "react-icons/im";
import { MdRemoveRedEye } from "react-icons/md";
import { useFormik } from "formik";
import { removeLeadingZeros } from "../../../utils/helpers/filter";
import {
  deleteRiskCover,
  getSingleAppliedPolicyCovers,
  resetRiskCover,
} from "../../../redux/slice/policy/policySlice";
import moment from "moment";
import { policyApplication } from "../../../utils/validators/schema";
import { policyValidation } from "../../../utils/validators/validators";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import EditRiskCovered from "./../../modals/policy/EditRiskCovered";
import PreviewPolicyAppSchedule from "../../modals/preview/PreviewPolicyAppSchedule";
import Select from "../../form/select/Select";
import { categories } from "../../../utils/data/account_type";
import useFileUpload from "../../../utils/hooks/useFileUpload";
import { forwardRef } from "react";
import { roundUpTens } from "../../../utils/helpers/functions";
import { useRef } from "react";
import { useSignature } from "../../../utils/hooks/useSignature";
import { flashMessage } from "../../../utils/flash";

const PolicyInformation = forwardRef(
  ({ policyDetails, covers, setUnderWritten, isNext, setIsNext }, ref) => {
    const fileTypes = ["JPG", "PNG", "PDF", "DOC", "DOCX"];
    const { user } = useSelector((state) => state.auth?.user);
    const { temp_policy_attach_data, risk_covered } = useSelector(
      (state) => state.policy
    );
    const { all_covers } = useSelector((state) => state.cover);

    const [editData, setEditData] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [dbRiskCovers, setDbRiskCovers] = useState(null);
    const [riskError, setRiskError] = useState("");
    const [endDate, setEndDate] = useState(policyDetails?.endDate);
    const [riskCovered, setRiskCovered] = useState(risk_covered);
    const [applyTo, setApplyTo] = useState(() => {
      let applyTo;
      if (temp_policy_attach_data) {
        if (temp_policy_attach_data?.repayment_frequency) {
          applyTo = "OO";
        } else {
          applyTo = "INS";
        }
      }
      return applyTo;
    });
    const { fileUrl, singleFileUpload } = useFileUpload();
    const [isUploading, setIsUploading] = useState(false);
    const [partPremium, setPartPremium] = useState(null);

    const {
      value: sign,
      Signature,
      clearSignature,
      saveSignature,
    } = useSignature();

    const dbRiskCoverRearranged = dbRiskCovers?.map((item) => ({
      cover_type: item.policyCoverType,
    }));

    const dispatch = useDispatch();
    const nextRef = useRef();

    const handleFileChange = (file) => {
      setIsUploading(true);
      singleFileUpload(file);
    };

    const handleEditData = (value) => {
      const indexOfRisk = dbRiskCovers?.findIndex(
        (item) => item.id === value.id
      );
      let data = [...dbRiskCovers];
      if (indexOfRisk !== -1) {
        data[indexOfRisk] = value;
        setDbRiskCovers(data);
      }
    };

    const onSubmit = (values) => {
      if (!sign) {
        flashMessage("error", "Underwriter signature is required");
        return;
      }

      const { agent_name, customer_name, policy_app_number, ...rest } =
        previewData;
      let payload = {
        ...rest,
        policy_app_no: policy_app_number,
        under_writer_date:
          values?.underwrittenDate || moment(new Date()).format("YYYY-MM-DD"),
        staff_id: user?.staff_id,
        part_premium: roundUpTens(partPremium),
        risks_cover: dbRiskCovers
          ?.map((item) => ({
            annual_premium: item?.annualPremium,
            cover_type: item?.policyCoverType,
            cover_for: item?.coverFor,
            sum_insured: item?.sumInsured,
            rate: item?.rate,
            description: item?.description,
          }))
          .concat(riskCovered),
        policy_id: dbRiskCovers[0]?.policy_id,
        underwriter_signature: sign,
        proposal_document: fileUrl || policyDetails?.proposal_document,
      };

      if (
        !rest?.repayment_frequency ||
        !rest?.duration ||
        !rest?.effective_date
      ) {
        return;
      }

      setUnderWritten(payload);
      ref.current?.click();
    };

    const {
      values,
      errors,
      touched,
      handleBlur,
      handleChange,
      handleSubmit,
      setFieldValue,
      setFieldTouched,
    } = useFormik({
      enableReinitialize: true,
      initialValues: {
        duration: policyDetails?.duration,
        effective_date: moment(policyDetails?.effectiveDate).format(
          "YYYY-MM-DD"
        ),
        repayment_frequency: policyDetails?.repayment_frequency,
        discount: policyDetails?.discount,
        but_charge: policyDetails?.butCharge,
        proposal_document: "",
        repayment_frequency: temp_policy_attach_data?.repayment_frequency
          ? temp_policy_attach_data?.repayment_frequency
          : policyDetails?.repayment_frequency,
        total_annual_premium: policyDetails?.totalPremium,
      },
      validationSchema: policyApplication,

      validate: (values) => policyValidation(values, true),
      onSubmit,
    });

    // date manipulation starts here
    const calculateEndDate = () => {
      const effectiveDateObj = moment(values?.effective_date);
      const EndDateObj = effectiveDateObj
        .add(values?.duration, "months")
        .subtract(1, "days"); // Subtract 1 day
      const EndDateFormatted = EndDateObj.format("YYYY-MM-DD");
      setEndDate(EndDateFormatted);
    };

    const totalPremium = useMemo(() => {
      let totalPremium = 0;
      if (riskCovered?.length > 0 || dbRiskCovers?.length > 0) {
        let total = dbRiskCovers?.reduce((acc, totalPremium) => {
          let calc =
            parseFloat(totalPremium?.sumInsured) *
            parseFloat(totalPremium.rate) *
            0.01;
          return (acc += calc);
        }, 0);
        let total2 = riskCovered?.reduce((acc, totalPremium) => {
          let calc =
            parseFloat(totalPremium?.sum_insured) *
            parseFloat(totalPremium.rate) *
            0.01;
          return (acc += calc);
        }, 0);
        totalPremium = total + total2;
      }
      if (values.discount) {
        totalPremium -= totalPremium * values.discount * 0.01;
      }
      setFieldValue("total_annual_premium", totalPremium);
      return totalPremium;
    }, [values.discount, riskCovered, dbRiskCovers]);

    useEffect(() => {
      if (!policyDetails) {
        dispatch(resetRiskCover());
      } else {
        if (covers) {
          dispatch(
            getSingleAppliedPolicyCovers(parseInt(covers[0]?.policy_id))
          );
          setDbRiskCovers(covers);
        }
        setApplyTo(
          policyDetails?.repayment_frequency === "one-off" ? "OO" : "INS"
        );
      }
    }, [dispatch, covers]);

    useEffect(() => {
      setRiskCovered(risk_covered);
    }, [risk_covered]);

    useEffect(() => {
      if (values?.effective_date || values?.duration) {
        calculateEndDate();
      }
      if (fileUrl) {
        setFieldValue("proposal_document", fileUrl);
        setIsUploading(false);
      }
    }, [values?.effective_date, values?.duration, fileUrl]);

    useEffect(() => {
      setPreviewData({
        ...values,
        customer_id: policyDetails?.customerId,
        customer_name:
          policyDetails?.customerFirstName +
          " " +
          policyDetails?.customerLastName,
        agent_name:
          policyDetails?.agentFirstName + " " + policyDetails?.agentLastName,
        policy_app_number: policyDetails?.policyAppNumber,
        office_name: policyDetails?.office_name,
        end_date: endDate,
      });
      setUnderWritten(values);
    }, [values, policyDetails, endDate]);

    useEffect(() => {
      if (isNext) {
        nextRef.current.click();
        setIsNext(false);
      }
    }, [isNext]);

    return (
      <div>
        <div className='col-md-12'>
          <div className='row mb-3'>
            <div className='col-md-4'>
              <label className='form-label'>Duration (in months)</label>
              <input
                type='number'
                id='duration'
                name='duration'
                className={`form-control form-control-sm no-arrows`}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values?.duration}
                onInput={(e) => removeLeadingZeros(e.target.id)}
              />
              {errors?.duration && touched?.duration && (
                <span className='text-danger'>{errors?.duration}</span>
              )}
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Effective date</label>
              <input
                type='date'
                id='effective_date'
                name='effective_date'
                className={`form-control form-control-sm`}
                value={values?.effective_date}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors?.effective_date && touched?.effective_date && (
                <span className='text-danger'>{errors?.effective_date}</span>
              )}
            </div>
            <div className='col-md-4'>
              <label className='form-label'>End date</label>
              <input
                type='date'
                id='end_date'
                name='end_date'
                value={endDate}
                className={`form-control form-control-sm`}
                disabled
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-4'>
              <label className='form-label'>Discount(%)</label>
              <input
                type='number'
                id='discount'
                name='discount'
                value={values?.discount}
                className={`form-control form-control-sm no-arrows`}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors?.discount && touched?.discount && (
                <span className='text-danger'>{errors?.discount}</span>
              )}
            </div>
            <div className='col-md-4'>
              <label className='form-label'>But-Charge</label>
              <input
                type='number'
                id='but_charge'
                name='but_charge'
                className={`form-control form-control-sm no-arrows`}
                value={values?.but_charge}
                onChange={handleChange}
                onBlur={handleBlur}
                onInput={(e) => removeLeadingZeros(e.target.id)}
              />
              {errors?.but_charge && touched?.but_charge && (
                <span className='text-danger'>{errors?.but_charge}</span>
              )}
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Total Annual Premium</label>
              <input
                type='text'
                id='total_premium'
                name='total_premium'
                value={parseFloat(totalPremium).toFixed(2)}
                disabled
                className={`form-control form-control-sm`}
              />
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col-md-4'>
              <div className='mb-3'>
                <label className='form-label'>
                  Repayment Frequency <span className='text-danger'>*</span>
                </label>
                <div className='form-check my-1'>
                  <input
                    className='form-check-input me-2'
                    type='radio'
                    name='apply_to'
                    id='apply_to'
                    onChange={(e) => {
                      setApplyTo("OO");
                      setFieldValue("repayment_frequency", "one-off");
                    }}
                    checked={applyTo === "OO"}
                  />
                  <label className='form-check-label' htmlFor='apply_to'>
                    One-off
                  </label>
                </div>
                <div className='form-check'>
                  <input
                    className='form-check-input me-2'
                    type='radio'
                    name='apply_to'
                    id='apply_to'
                    checked={applyTo === "INS"}
                    onChange={() => {
                      setApplyTo("INS");
                      setFieldValue("repayment_frequency", "");
                      setFieldTouched("repayment_frequency", false);
                    }}
                  />
                  <label
                    className='form-check-label mb-1'
                    htmlFor='repayment_frequency'
                  >
                    Installments
                  </label>
                  {applyTo === "INS" && (
                    <div>
                      <Select
                        isError={
                          errors?.repayment_frequency &&
                          touched?.repayment_frequency
                        }
                        name='repayment_frequency'
                        data={categories?.map((item) => ({
                          label: item?.name,
                          value: item?.name,
                        }))}
                        value={values.repayment_frequency}
                        onChange={(selectedOption) =>
                          setFieldValue(
                            "repayment_frequency",
                            selectedOption?.value
                          )
                        }
                        onBlur={() => {
                          handleBlur({
                            target: { name: "repayment_frequency" },
                          });
                        }}
                      />
                      {errors?.repayment_frequency &&
                        touched?.repayment_frequency && (
                          <p className='text-danger mb-0'>
                            {errors?.repayment_frequency}
                          </p>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-md-4'>
              <label className='form-label d-block' htmlFor='proposal_document'>
                <span className={`${fileUrl && "text-success"}`}>
                  Upload Proposal document
                </span>
                {!fileUrl && isUploading && (
                  <span>
                    <i className='mdi mdi-dots-circle mdi-spin '></i>{" "}
                  </span>
                )}
                {fileUrl && (
                  <span>
                    <i className='mdi mdi-check text-success fs-5'></i>
                  </span>
                )}
              </label>
              <FileUploader
                label='Browse Document'
                children={
                  <div
                    className='borde border-dotted p-1 px-2 text-center cursor-p'
                    style={{ border: "2px dashed grey" }}
                  >
                    <i className='uil uil-cloud-upload fs-4'></i> Upload
                    Document
                    <span className='text-primary-dark mt-0'></span>
                  </div>
                }
                handleChange={handleFileChange}
                onDrop={handleFileChange}
                name='file'
                types={fileTypes}
              />
            </div>
          </div>
          <div className=' gap-3 d-flex'>
            <div>
              <span
                className='btn btn-primary text-white px-4'
                data-bs-toggle='modal'
                data-bs-target='#add_risk_covered2'
              >
                Add Risk Covered
              </span>
            </div>
            <div>
              <span
                className='btn btn-primary text-white px-4 d-flex gap-2 align-items-center'
                data-bs-toggle='modal'
                data-bs-target='#more_document_modal'
              >
                <span>Preview Payment Plan</span>
                <MdRemoveRedEye
                  className='text-white'
                  style={{
                    fontSize: "20px",
                  }}
                />
              </span>
            </div>
            {fileUrl ||
            temp_policy_attach_data?.proposal_document ||
            policyDetails?.proposal_document ? (
              <div>
                <a
                  href={
                    fileUrl ||
                    temp_policy_attach_data?.proposal_document ||
                    policyDetails?.proposal_document
                  }
                  target='_blank'
                  className='btn btn-sm btn-primary text-white d-flex gap-2 align-items-center align-content-center justify-content-center'
                >
                  View Proposal Document
                  <i className='mdi mdi-eye text-white fs-5'></i>
                </a>
              </div>
            ) : null}
          </div>
          <div className='row mt-4'>
            <div className='col-md-12 table-responsive-sm'>
              <table className='table table-centered table-striped nowrap w-100 no-footer'>
                <thead>
                  <tr>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "130.8px" }}
                      aria-label='Customer: activate to sort column ascending'
                    >
                      Policy Cover Type
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "140.8px" }}
                      aria-label='Customer: activate to sort column ascending'
                    >
                      Cover For
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "108.8px" }}
                      aria-label='Phone: activate to sort column ascending'
                    >
                      Rate(%)
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "147.8px" }}
                    >
                      Sum Insured
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "147.8px" }}
                      aria-label='Email: activate to sort column ascending'
                    >
                      Annual Premium
                    </th>
                    <th
                      className='sorting'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "246.8px" }}
                      aria-label='Location: activate to sort column ascending'
                    >
                      Detail Description / Specification
                    </th>
                    <th
                      className='sorting sorting_asc'
                      tabIndex='0'
                      rowSpan='1'
                      colSpan='1'
                      style={{ width: "146.8px" }}
                    ></th>
                  </tr>
                </thead>

                <tbody>
                  {dbRiskCovers?.map((item, i) => (
                    <tr key={item?.id}>
                      <td>
                        {
                          all_covers?.unpaginated_cover?.filter(
                            (p) =>
                              String(p.id) === String(item?.policyCoverType)
                          )[0]?.name
                        }
                      </td>
                      <td>{item?.coverFor}</td>
                      <td className='table-user'>{item?.rate}</td>
                      <td className='table-user'>{item?.sumInsured}</td>
                      <td className='text-dark'>
                        {parseFloat(item?.annualPremium).toFixed(2)}
                      </td>
                      <td>{item?.description}</td>

                      <td className='text-center'>
                        <div className='d-flex gap-2 align-content-center justify-content-center'>
                          <span
                            className='text-primary cursor-p'
                            data-bs-toggle='modal'
                            data-bs-target='#add_risk_covered2'
                            onClick={() => {
                              setEditData({
                                cover_type: item?.policyCoverType,
                                policy_id: item?.policy_id,
                                cover_for: item?.coverFor,
                                id: item?.id,
                                rate: item?.rate,
                                sum_insured: item?.sumInsured,
                                annual_premium: item?.annualPremium,
                                description: item?.description,
                              });
                            }}
                          >
                            <BsPencilFill size={17} />
                          </span>
                          <span className='text-danger cursor-p p-2'></span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {riskCovered?.map((item, i) => (
                    <tr key={i}>
                      <td>
                        {
                          all_covers?.unpaginated_cover?.filter(
                            (p) => String(p.id) === String(item?.cover_type)
                          )[0]?.name
                        }
                      </td>
                      <td>{item?.cover_for}</td>
                      <td className='table-user'>{item?.rate}</td>
                      <td className='table-user'>{item?.sum_insured}</td>
                      <td className='text-dark'>{item?.annual_premium}</td>
                      <td>{item?.description}</td>

                      <td className='text-center'>
                        <div className='d-flex gap-2 align-content-center justify-content-center'>
                          <span
                            className='text-primary cursor-p'
                            data-bs-toggle='modal'
                            data-bs-target='#add_risk_covered2'
                            onClick={() => {
                              setEditData(item);
                            }}
                          >
                            <BsPencilFill size={17} />
                          </span>
                          <span
                            className='text-danger cursor-p'
                            onClick={() => dispatch(deleteRiskCover(item?.id))}
                          >
                            <ImBin size={17} />
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className='row mb-3 mt-2'>
            <div className='col-md-12'>
              <label className='form-label'>Detailed Specifications</label>
              <textarea
                id='detailed_specification'
                name='detailed_specification'
                value={values?.detailed_specification}
                className={`form-control no-arrows`}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className='row mb-2'>
            <div className='col-md-6 mt-2'>
              <label htmlFor='inputEmail4' className='form-label d-block'>
                Underwriter's signature
                {sign && (
                  <span className='text-success'>
                    <BsCheck size={22} />
                  </span>
                )}
              </label>
              <div className='d-flex gap-3'>{Signature}</div>
              <div className='d-flex gap-2 mt-2'>
                <button
                  type='button'
                  className='btn btn-sm btn-secondary'
                  onClick={() => clearSignature()}
                >
                  Clear signature
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-info'
                  onClick={() => saveSignature()}
                >
                  Sign
                </button>
              </div>
            </div>
          </div>
          <div className='d-flex justify-content-end mb-2'>
            <button
              type='submit'
              ref={nextRef}
              className='btn btn-sm btn-primary d-flex gap-1 align-items-center'
              onClick={() => {
                handleSubmit(values);
              }}
            >
              <span>Next</span>
              <BsArrowRight />
            </button>
          </div>
        </div>
        <EditRiskCovered
          modal_id='add_risk_covered2'
          editData={editData}
          setEditData={setEditData}
          selectedRisks={dbRiskCoverRearranged?.concat(riskCovered)}
          title={editData ? "Edit Risk Cover" : "Add Risk Cover"}
          handleEditData={handleEditData}
        />
        <PreviewPolicyAppSchedule
          modal_id='more_document_modal'
          edit
          data={previewData}
          setPartPremium={setPartPremium}
        />
      </div>
    );
  }
);

export default PolicyInformation;
